import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { SlideDeck, SLIDE_COUNT } from "@/components/SlideDeck";
import { IntroScreen } from "@/components/IntroScreen";
import { Hud } from "@/components/Hud";
import { VoiceOverlay } from "@/components/VoiceOverlay";
import { QAPanel } from "@/components/QAPanel";
import { Captions, segmentCaptions } from "@/components/Captions";
import { useSpeech } from "@/hooks/useSpeech";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LAST = SLIDE_COUNT - 1;
const YES = /\b(yes|yeah|yep|yup|sure|correct|absolutely|of course|please|go ahead|continue|that answers|perfect|great|it did)\b/i;
const NO = /\b(no|nope|not really|not quite|didn't|did not|negative)\b/i;
const PROMPT_KEYS = ["confirm", "continue_q", "elaborate", "resume", "more_questions"];

const toSrc = (b64) => `data:audio/mpeg;base64,${b64}`;
const SILENCE = "data:audio/wav;base64,UklGRigAAABXQVZFZm10IBAAAAABAAEAIlYAAESsAAACABAAZGF0YQQAAAAAAAAA";

export default function Presentation() {
  const [phase, setPhase] = useState("intro");
  const [slide, setSlide] = useState(0);
  const [introStage, setIntroStage] = useState("idle");
  const [liveQuestion, setLiveQuestion] = useState("");
  const [answerText, setAnswerText] = useState("");
  const [chatLog, setChatLog] = useState([]);
  const [caption, setCaption] = useState("");
  const [captionsOn, setCaptionsOn] = useState(true);

  const phaseRef = useRef(phase);
  phaseRef.current = phase;
  const slideRef = useRef(slide);
  slideRef.current = slide;

  const narrationEl = useRef(null);
  const voiceEl = useRef(null);
  const narrCache = useRef({});
  const prompts = useRef({});
  const spokenText = useRef("");
  const bufRef = useRef("");
  const silenceTimer = useRef(null);
  const playToken = useRef(0);
  const captionChunks = useRef(null);
  const recentTexts = useRef([]);
  const xenaStoppedAt = useRef(0);
  const isEchoRef = useRef(null);
  const resumeFns = useRef({});
  const typedAsk = useRef(false);
  const sessionId = useRef(`session-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`);

  useEffect(() => {
    narrationEl.current = new Audio();
    voiceEl.current = new Audio();
    const el = narrationEl.current;
    const onTime = () => {
      const d = el.duration;
      const chunks = captionChunks.current;
      if (!d || !chunks || !chunks.length) return;
      const f = el.currentTime / d;
      const c = chunks.find((ch) => f >= ch.start && f < ch.end) || chunks[chunks.length - 1];
      setCaption((prev) => (prev === c.text ? prev : c.text));
    };
    el.addEventListener("timeupdate", onTime);
    const markStop = () => { xenaStoppedAt.current = Date.now(); };
    const vEl = voiceEl.current;
    [el, vEl].forEach((a) => {
      a.addEventListener("pause", markStop);
      a.addEventListener("ended", markStop);
    });
    return () => {
      el.removeEventListener("timeupdate", onTime);
      [el, vEl].forEach((a) => {
        a.removeEventListener("pause", markStop);
        a.removeEventListener("ended", markStop);
      });
      narrationEl.current.pause();
      voiceEl.current.pause();
    };
  }, []);

  const setXenaText = (text) => {
    spokenText.current = text;
    const arr = recentTexts.current;
    if (arr[arr.length - 1] !== text) arr.push(text);
    if (arr.length > 4) arr.shift();
  };

  const fetchNarration = useCallback(async (i) => {
    if (narrCache.current[i]) return narrCache.current[i];
    const { data } = await axios.get(`${API}/narration/${i}`);
    narrCache.current[i] = data;
    return data;
  }, []);

  const playNarration = useCallback(async (i) => {
    const token = ++playToken.current;
    voiceEl.current.pause();
    setSlide(i);
    slideRef.current = i;
    setPhase("narrating");
    phaseRef.current = "narrating";
    try {
      const data = await fetchNarration(i);
      if (playToken.current !== token || phaseRef.current !== "narrating") return;
      setXenaText(data.text);
      captionChunks.current = segmentCaptions(data.text);
      setCaption("");
      const el = narrationEl.current;
      el.src = toSrc(data.audio);
      el.onended = () => {
        spokenText.current = "";
        setCaption("");
        if (slideRef.current < LAST) playNarration(slideRef.current + 1);
        else setPhase("qa");
      };
      await el.play();
      if (i < LAST) fetchNarration(i + 1).catch(() => {});
    } catch (e) {
      console.error("Narration failed", e);
    }
  }, [fetchNarration]);

  const playPrompt = useCallback(async (key, nextPhase, onEnd) => {
    narrationEl.current.pause();
    let p = prompts.current[key];
    if (!p) {
      try {
        const { data } = await axios.get(`${API}/prompt/${key}`);
        prompts.current[key] = data;
        p = data;
      } catch (e) {
        if (nextPhase) setPhase(nextPhase);
        if (onEnd) onEnd();
        return;
      }
    }
    setXenaText(p.text);
    const el = voiceEl.current;
    el.src = toSrc(p.audio);
    el.onended = () => {
      spokenText.current = "";
      if (onEnd) onEnd();
    };
    if (nextPhase) setPhase(nextPhase);
    el.play().catch(() => {
      if (onEnd) onEnd();
    });
  }, []);

  const clearSilence = () => {
    if (silenceTimer.current) clearTimeout(silenceTimer.current);
    silenceTimer.current = null;
  };

  const submitQuestion = useCallback(async () => {
    clearSilence();
    const q = bufRef.current.trim();
    bufRef.current = "";
    if (!q) {
      setPhase("listening");
      return;
    }
    const fromVoice = !typedAsk.current;
    typedAsk.current = false;
    if (fromVoice && isEchoRef.current && isEchoRef.current(q, 0.65)) {
      setLiveQuestion("");
      const atEnd = slideRef.current === LAST && narrationEl.current.ended;
      if (atEnd) setPhase("qa");
      else if (resumeFns.current.narration) resumeFns.current.narration();
      return;
    }
    setLiveQuestion("");
    setPhase("thinking");
    setChatLog((l) => [...l, { role: "user", text: q }]);
    try {
      const { data } = await axios.post(`${API}/ask`, { session_id: sessionId.current, question: q });
      setChatLog((l) => [...l, { role: "assistant", text: data.answer }]);
      setAnswerText(data.answer);
      if (data.audio) {
        setXenaText(data.answer);
        const el = voiceEl.current;
        el.src = toSrc(data.audio);
        el.onended = () => {
          spokenText.current = "";
          playPrompt("confirm", "confirming");
        };
        setPhase("answering");
        await el.play();
      } else {
        setPhase("confirming");
      }
    } catch (e) {
      console.error("Ask failed", e);
      setAnswerText("I'm sorry — I couldn't reach the dossier just now. Please try once more.");
      setPhase("confirming");
    }
  }, [playPrompt]);

  const armSilence = useCallback(() => {
    clearSilence();
    silenceTimer.current = setTimeout(submitQuestion, 2500);
  }, [submitQuestion]);

  const startListening = useCallback((seed) => {
    narrationEl.current.pause();
    voiceEl.current.pause();
    spokenText.current = "";
    bufRef.current = seed || "";
    setLiveQuestion(bufRef.current);
    setAnswerText("");
    setPhase("listening");
    if (bufRef.current) armSilence();
  }, [armSilence]);

  const resumeNarration = useCallback(() => {
    setPhase("narrating");
    phaseRef.current = "narrating";
    const data = narrCache.current[slideRef.current];
    if (data) setXenaText(data.text);
    narrationEl.current.play().catch(() => playNarration(slideRef.current));
  }, [playNarration]);

  const handleConfirmYes = useCallback(() => {
    clearSilence();
    if (slideRef.current === LAST) playPrompt("more_questions", "qa");
    else playPrompt("continue_q", "awaiting_continue");
  }, [playPrompt]);

  const handleConfirmNo = useCallback(() => {
    clearSilence();
    playPrompt("elaborate", null, () => startListening(""));
  }, [playPrompt, startListening]);

  const handleContinueYes = useCallback(() => {
    clearSilence();
    playPrompt("resume", null, () => resumeNarration());
  }, [playPrompt, resumeNarration]);

  const isEcho = useCallback((t, threshold) => {
    const norm = (s) => s.toLowerCase().replace(/[^a-z0-9\s']/g, " ").split(/\s+/).filter(Boolean);
    const sw = norm(recentTexts.current.join(" "));
    if (!sw.length) return false;
    const tw = norm(t);
    if (!tw.length) return true;
    if (tw.length === 1) return sw.includes(tw[0]);
    const bigrams = new Set();
    for (let i = 0; i < sw.length - 1; i++) bigrams.add(`${sw[i]} ${sw[i + 1]}`);
    let hits = 0;
    for (let i = 0; i < tw.length - 1; i++) {
      if (bigrams.has(`${tw[i]} ${tw[i + 1]}`)) hits++;
    }
    return hits / (tw.length - 1) >= threshold;
  }, []);

  const recentlyStopped = () => Date.now() - xenaStoppedAt.current < 4000;

  const handleFinal = useCallback((t) => {
    const p = phaseRef.current;
    switch (p) {
      case "listening":
        if (isEcho(t, recentlyStopped() ? 0.6 : 0.8)) return;
        bufRef.current = `${bufRef.current} ${t}`.trim();
        setLiveQuestion(bufRef.current);
        armSilence();
        break;
      case "confirming":
        if (isEcho(t, recentlyStopped() ? 0.6 : 0.8)) return;
        if (NO.test(t) && !YES.test(t)) handleConfirmNo();
        else if (YES.test(t)) handleConfirmYes();
        else startListening(t);
        break;
      case "awaiting_continue":
        if (isEcho(t, recentlyStopped() ? 0.6 : 0.8)) return;
        if (YES.test(t)) handleContinueYes();
        else if (NO.test(t)) startListening("");
        else startListening(t);
        break;
      case "qa":
        if (isEcho(t, recentlyStopped() ? 0.6 : 0.8)) return;
        startListening(t);
        break;
      default:
        break;
    }
  }, [isEcho, startListening, armSilence, handleConfirmNo, handleConfirmYes, handleContinueYes]);

  const speechEnabled = ["listening", "confirming", "awaiting_continue", "qa"].includes(phase);
  const { supported: micSupported, interim } = useSpeech(handleFinal, speechEnabled);
  const micSupportedRef = useRef(true);
  micSupportedRef.current = micSupported;
  isEchoRef.current = isEcho;
  resumeFns.current = { narration: resumeNarration };

  const begin = useCallback(async () => {
    setIntroStage("preparing");
    axios.post(`${API}/narration/prefetch`).catch(() => {});
    PROMPT_KEYS.forEach((k) =>
      axios.get(`${API}/prompt/${k}`).then(({ data }) => { prompts.current[k] = data; }).catch(() => {})
    );
    try {
      await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch (e) {
      console.warn("Mic permission denied — voice questions disabled");
    }
    try {
      await fetchNarration(0);
    } catch (e) {
      console.error(e);
    }
    setIntroStage("ready");
  }, [fetchNarration]);

  const start = useCallback(() => {
    const v = voiceEl.current;
    v.src = SILENCE;
    v.play().then(() => v.pause()).catch(() => {});
    if (!micSupportedRef.current) {
      toast("Voice input isn't supported in this browser", {
        description: "Use the Ask button and type your questions instead.",
        duration: 6000,
      });
    }
    playNarration(0);
  }, [playNarration]);

  const togglePlay = useCallback(() => {
    if (phaseRef.current === "narrating") {
      narrationEl.current.pause();
      setPhase("paused");
    } else if (phaseRef.current === "paused") {
      resumeNarration();
    }
  }, [resumeNarration]);

  const jump = useCallback((delta) => {
    const target = Math.min(LAST, Math.max(0, slideRef.current + delta));
    if (target === slideRef.current) return;
    clearSilence();
    voiceEl.current.pause();
    playNarration(target);
  }, [playNarration]);

  const typedQuestion = useCallback((q) => {
    clearSilence();
    typedAsk.current = true;
    bufRef.current = q;
    submitQuestion();
  }, [submitQuestion]);

  const cancelOverlay = useCallback(() => {
    clearSilence();
    bufRef.current = "";
    setLiveQuestion("");
    voiceEl.current.pause();
    spokenText.current = "";
    if (slideRef.current === LAST) setPhase("qa");
    else resumeNarration();
  }, [resumeNarration]);

  const onYesBtn = useCallback(() => {
    if (phaseRef.current === "confirming") handleConfirmYes();
    else handleContinueYes();
  }, [handleConfirmYes, handleContinueYes]);

  const onNoBtn = useCallback(() => {
    if (phaseRef.current === "confirming") handleConfirmNo();
    else startListening("");
  }, [handleConfirmNo, startListening]);

  return (
    <div className="fixed inset-0 bg-[#050505] overflow-hidden" data-testid="presentation-root">
      <Toaster position="top-center" theme="dark" />
      <div className="noise-overlay" />
      <div className="ambient-glow -top-40 -right-40" />
      <div className="ambient-glow -bottom-64 -left-40" style={{ animationDelay: "-12s" }} />

      {phase === "intro" ? (
        <IntroScreen stage={introStage} onBegin={begin} onStart={start} />
      ) : (
        <>
          <SlideDeck index={slide} />
          {captionsOn && ["narrating", "paused"].includes(phase) && <Captions text={caption} />}
          <Hud
            slide={slide}
            total={SLIDE_COUNT}
            phase={phase}
            micLive={micSupported && speechEnabled}
            captionsOn={captionsOn}
            onToggleCaptions={() => setCaptionsOn((v) => !v)}
            onTogglePlay={togglePlay}
            onPrev={() => jump(-1)}
            onNext={() => jump(1)}
            onAsk={() => startListening("")}
          />
          <VoiceOverlay
            phase={phase}
            liveQuestion={liveQuestion}
            interim={phase === "listening" ? interim : ""}
            answerText={answerText}
            onCancel={cancelOverlay}
            onTypedQuestion={typedQuestion}
            onYes={onYesBtn}
            onNo={onNoBtn}
          />
          {phase === "qa" && (
            <QAPanel chatLog={chatLog} onTypedQuestion={typedQuestion} micLive={micSupported} />
          )}
        </>
      )}
    </div>
  );
}
